import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { spotifyService, SpotifyTrack } from '@/services/spotifyService';
import { SequenceWithMetadata } from '@/hooks/useAdminSequences';
import { Search, Music, Clock, TrendingUp, ExternalLink, Volume2 } from 'lucide-react';
import { toast } from 'sonner';

interface SpotifySearchSectionProps {
  onTrackSelect: (track: SpotifyTrack) => void;
  currentSequence?: SequenceWithMetadata;
  defaultSearchTerm?: string;
}

export function SpotifySearchSection({ 
  onTrackSelect, 
  currentSequence, 
  defaultSearchTerm 
}: SpotifySearchSectionProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<SpotifyTrack[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState<SpotifyTrack | null>(null);

  // Set default search term when component mounts or sequence changes
  useEffect(() => {
    if (defaultSearchTerm) {
      setSearchTerm(defaultSearchTerm);
    } else if (currentSequence?.title) {
      setSearchTerm(currentSequence.title);
    }
  }, [defaultSearchTerm, currentSequence]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    setSearching(true);
    try {
      const results = await spotifyService.searchTracks(searchTerm, 10);
      setSearchResults(results);
      
      if (results.length === 0) {
        toast.info('No tracks found for your search');
      }
    } catch (error) {
      console.error('Spotify search error:', error);
      toast.error('Failed to search Spotify. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleTrackSelect = (track: SpotifyTrack) => {
    setSelectedTrack(track);
    onTrackSelect(track);
    toast.success(`Selected "${track.name}" by ${track.artists[0]?.name}`);
  };

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Search className="h-5 w-5" />
          Spotify Search & Metadata
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Search Input */}
        <div className="space-y-2">
          <Label htmlFor="spotify-search">Search Spotify</Label>
          <div className="flex gap-2">
            <Input
              id="spotify-search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Enter song name and artist"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button 
              onClick={handleSearch} 
              disabled={searching || !searchTerm.trim()}
            >
              {searching ? 'Searching...' : 'Search'}
            </Button>
          </div>
          {currentSequence?.title && (
            <p className="text-sm text-muted-foreground">
              Auto-populated from sequence: "{currentSequence.title}"
            </p>
          )}
        </div>

        {/* Current Sequence Spotify Data */}
        {currentSequence?.spotify_track_id && (
          <Alert>
            <Music className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-1">
                <p className="font-medium">Current Spotify Data:</p>
                <p className="text-sm">Track ID: {currentSequence.spotify_track_id}</p>
                {currentSequence.spotify_popularity && (
                  <p className="text-sm">Popularity: {currentSequence.spotify_popularity}/100</p>
                )}
                {currentSequence.album && (
                  <p className="text-sm">Album: {currentSequence.album}</p>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-medium">Search Results ({searchResults.length})</h4>
            <div className="max-h-96 overflow-y-auto space-y-2">
              {searchResults.map((track) => (
                <Card 
                  key={track.id} 
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    selectedTrack?.id === track.id ? 'ring-2 ring-primary bg-primary/5' : ''
                  }`}
                  onClick={() => handleTrackSelect(track)}
                >
                  <CardContent className="p-4">
                    <div className="flex gap-3">
                      {/* Album Art */}
                      {track.album.images[0] && (
                        <img
                          src={track.album.images[0].url}
                          alt={track.album.name}
                          className="w-16 h-16 rounded object-cover flex-shrink-0"
                        />
                      )}
                      
                      {/* Track Info */}
                      <div className="flex-1 min-w-0">
                        <h5 className="font-medium truncate">{track.name}</h5>
                        <p className="text-sm text-muted-foreground truncate">
                          {track.artists.map(a => a.name).join(', ')}
                        </p>
                        <p className="text-sm text-muted-foreground truncate">
                          {track.album.name}
                        </p>
                        
                        {/* Metadata */}
                        <div className="flex flex-wrap gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            <Clock className="h-3 w-3 mr-1" />
                            {formatDuration(track.duration_ms)}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            <TrendingUp className="h-3 w-3 mr-1" />
                            {track.popularity}/100
                          </Badge>
                          {track.explicit && (
                            <Badge variant="secondary" className="text-xs">
                              Explicit
                            </Badge>
                          )}
                          {track.preview_url && (
                            <Badge variant="outline" className="text-xs">
                              <Volume2 className="h-3 w-3 mr-1" />
                              Preview
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col justify-between">
                        <Button
                          variant="outline"
                          size="sm"
                          asChild
                        >
                          <a
                            href={track.external_urls.spotify}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </Button>
                        
                        {track.preview_url && (
                          <Button
                            variant="outline"
                            size="sm"
                            asChild
                            className="mt-1"
                          >
                            <a
                              href={track.preview_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Volume2 className="h-3 w-3" />
                            </a>
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Selected Track Summary */}
        {selectedTrack && (
          <Alert className="border-green-200 bg-green-50">
            <Music className="h-4 w-4 text-green-600" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-medium text-green-800">
                  Selected: {selectedTrack.name} by {selectedTrack.artists[0]?.name}
                </p>
                <div className="text-sm text-green-700 space-y-1">
                  <p>Album: {selectedTrack.album.name}</p>
                  <p>Duration: {formatDuration(selectedTrack.duration_ms)}</p>
                  <p>Popularity: {selectedTrack.popularity}/100</p>
                  <p>Spotify ID: {selectedTrack.id}</p>
                  {selectedTrack.album.images[0] && (
                    <p>Album Art: {selectedTrack.album.images[0].width}x{selectedTrack.album.images[0].height}</p>
                  )}
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* No Results Message */}
        {searchResults.length === 0 && searchTerm && !searching && (
          <Alert>
            <Search className="h-4 w-4" />
            <AlertDescription>
              No results found for "{searchTerm}". Try adjusting your search terms.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}